// SpendApprovalBanner (ticket 167) — slim informational bar shown to staff while
// the workspace is not approved to use money-costing features (AI, texting,
// email, e-signature, invitations). Everything else keeps working, so this is
// information, not a wall. Not dismissible: the state is the point.
import React, { useEffect, useRef, useState } from 'react';
import { Clock, Ban } from 'lucide-react';
import SpendApprovalRequestForm from './SpendApprovalRequestForm';

// Same address the landing page footer uses. Unset → the link opens the
// in-app feedback dialog instead (onContact).
const CONTACT_EMAIL = (process.env.REACT_APP_CONTACT_EMAIL || '').trim();
const OFF_TEXT = 'AI, texting, email and e-signature are off for this workspace.';
// Re-check while the workspace is waiting on a decision (item: a user sitting on
// a page must see the approval without a reload). No polling once approved.
export const SPEND_POLL_MS = 60000;
const linkCls = 'underline font-bold hover:opacity-80';

export default function SpendApprovalBanner({
  tenant, canRequest, onRequested, onContact, onRefresh, contactEmail = CONTACT_EMAIL,
}) {
  const [formOpen, setFormOpen] = useState(false);
  const status = tenant?.spend_approval;

  // Latest refresh fn without re-subscribing every render.
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const waiting = !!status && status !== 'approved';

  useEffect(() => {
    if (!waiting) return undefined;
    const refresh = () => { refreshRef.current?.(); };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    const timer = setInterval(refresh, SPEND_POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      clearInterval(timer);
    };
  }, [waiting]);

  if (!status || status === 'approved') return null;

  const denied = status === 'denied';
  const requested = !!tenant.spend_approval_requested_at;
  const Icon = denied ? Ban : Clock;
  const label = denied ? 'Not approved' : 'Awaiting approval';
  let text = OFF_TEXT;
  if (!denied) {
    text = requested
      ? 'Usually within one business day.'
      : 'AI, texting, email and e-signature turn on once your workspace is approved.';
  }
  let contact = null;
  if (denied && contactEmail) {
    contact = (
      <a href={`mailto:${contactEmail}?subject=${encodeURIComponent('Workspace approval')}`}
        className={linkCls} data-testid="spend-approval-contact">Contact support</a>
    );
  } else if (denied && onContact) {
    contact = (
      <button type="button" onClick={onContact} className={linkCls} data-testid="spend-approval-contact">
        Contact support
      </button>
    );
  } else if (denied) {
    contact = <span data-testid="spend-approval-contact">Contact support.</span>;
  }

  return (
    <div
      className="w-full px-3 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] font-medium"
      style={denied ? { background: '#FEE2E2', color: '#991B1B' } : { background: '#FEF3C7', color: '#92400E' }}
      role="status"
      data-testid="spend-approval-banner"
      data-status={status}
    >
      <Icon size={13} className="shrink-0" aria-hidden="true" />
      <span className="font-bold" data-testid="spend-approval-label">{label}</span>
      <span data-testid="spend-approval-text">{text}</span>
      {contact}
      {!denied && !requested && (canRequest ? (
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          data-testid="spend-approval-request-btn"
          className="text-[11px] font-bold px-2.5 py-0.5 rounded bg-white/90 hover:bg-white transition"
          style={{ color: '#92400E' }}
        >
          Request approval
        </button>
      ) : (
        <span data-testid="spend-approval-ask-owner">Ask your workspace owner to request approval.</span>
      ))}
      {formOpen && (
        <SpendApprovalRequestForm
          open={formOpen}
          onOpenChange={setFormOpen}
          onRequested={onRequested}
        />
      )}
    </div>
  );
}
